import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '@/lib/utils';
import { IconRefresh } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';

interface MonitoringStatus {
  status: 'healthy' | 'warning' | 'alert';
  message: string;
  expectedNotifications: number;
  actualNotifications: number;
  missingNotifications: number;
  lastNotificationTime: string | null;
  patternAnalysis: {
    peakHours: number[];
    averageDailyCount: number;
    currentDayCount: number;
  };
  alerts: string[];
}

interface PeakHourRange {
  from: number;
  to: number;
  hours: number[];
  averageCount: number;
}

interface DetailedStats {
  total: number;
  today: number;
  last7Days: number;
  averageDaily: number;
  peakHours: number[];
  peakHoursByDay?: Record<number, number[]>;
  peakHourRanges?: PeakHourRange[];
  peakHourRangesByDay?: Record<number, PeakHourRange[]>;
  hourlyPatterns: Array<{ hour: number; averageCount: number }>;
  hourlyPatternsByDay?: Record<number, Array<{ hour: number; averageCount: number }>>;
  dailyAverages: Record<number, number>;
  byDate: Array<{ date: string; count: number }>;
  lastNotification: string | null;
}

export function DmaMonitorPage() {
  const { hasPageAccess } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<MonitoringStatus | null>(null);
  const [stats, setStats] = useState<DetailedStats | null>(null);

  const handleRefresh = async () => {
    if (!hasPageAccess('monitoring')) return;
    
    setIsLoading(true);
    setStatsLoading(true);
    setError(null);
    
    try {
      const [statusResp, statsResp] = await Promise.all([
        api.get('/monitoring/dma-monitor/status'),
        api.get('/monitoring/dma-monitor/stats'),
      ]);

      const statusData = statusResp.data?.data;
      const statsData = statsResp.data?.data;

      if (statusData) {
        setStatus(statusData);
      }
      if (statsData) {
        setStats(statsData);
      }
      setLastUpdatedAt(new Date());
    } catch (e: any) {
      setError('Failed to load DMA monitor data');
      console.error('Error loading DMA monitor:', e);
    } finally {
      setIsLoading(false);
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: handleRefresh is not wrapped in useCallback
  }, []);

  const getStatusBg = (s: string) => {
    switch (s) {
      case 'alert': return 'bg-red-50 dark:bg-red-950/30';
      case 'warning': return 'bg-amber-50 dark:bg-amber-950/30';
      case 'healthy': return 'bg-emerald-50 dark:bg-emerald-950/30';
      default: return '';
    }
  };

  const getStatusBadge = (s: string) => {
    switch (s) {
      case 'alert':
        return <Badge variant="destructive">{s.toUpperCase()}</Badge>;
      case 'warning':
        return <Badge variant="warning">{s.toUpperCase()}</Badge>;
      case 'healthy':
        return <Badge variant="success">{s.toUpperCase()}</Badge>;
      default:
        return <Badge variant="secondary">{s.toUpperCase()}</Badge>;
    }
  };

  // Convert GMT hour to GMT+2 hour and format as HH:00
  const formatHour = (hour: number) => {
    const gmtPlus2Hour = (hour + 2) % 24;
    return `${String(gmtPlus2Hour).padStart(2, '0')}:00`;
  };

  const formatNumber = (num: number | string): string => {
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue)) return String(num);
    return Math.round(numValue).toLocaleString('en-US');
  };

  const formatDateTimeLocal = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const gmtPlus2 = new Date(date.getTime() + 2 * 60 * 60 * 1000);
      const year = gmtPlus2.getUTCFullYear();
      const month = String(gmtPlus2.getUTCMonth() + 1).padStart(2, '0');
      const day = String(gmtPlus2.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      let utcYear = date.getUTCFullYear();
      let utcMonth = date.getUTCMonth();
      let utcDate = date.getUTCDate();
      let utcHours = date.getUTCHours();
      const utcMinutes = date.getUTCMinutes();
      
      let gmtPlus2Hours = utcHours + 2;
      
      if (gmtPlus2Hours >= 24) {
        gmtPlus2Hours = gmtPlus2Hours - 24;
        utcDate = utcDate + 1;
        const daysInMonth = new Date(utcYear, utcMonth + 1, 0).getDate();
        if (utcDate > daysInMonth) {
          utcDate = 1;
          utcMonth = utcMonth + 1;
          if (utcMonth > 11) {
            utcMonth = 0;
            utcYear = utcYear + 1;
          }
        }
      }
      
      const year = String(utcYear).padStart(4, '0');
      const month = String(utcMonth + 1).padStart(2, '0');
      const day = String(utcDate).padStart(2, '0');
      const hours = String(gmtPlus2Hours).padStart(2, '0');
      const minutes = String(utcMinutes).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  const dayNames: Record<number, string> = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
  };

  if (isLoading && !status && !stats) {
    return <PageLoader message="Loading DMA monitor data..." />;
  }

  return (
    <div>
      <PageHeader
        title="DMA/MSISDN Monitor"
        subtitle="Monitor MTN SIM activation notifications and detect delays"
        action={
          <Button
            onClick={handleRefresh}
            disabled={isLoading || statsLoading}
          >
            <IconRefresh className="size-4" />
            {isLoading || statsLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
        }
      />
      {lastUpdatedAt && (
        <span className="text-xs text-muted-foreground mb-4 block">
          Last updated: {formatDateTimeLocal(lastUpdatedAt.toISOString())}
        </span>
      )}

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {status && (
        <MainCard className={cn('mb-6', getStatusBg(status.status))}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Current Status</h3>
            {getStatusBadge(status.status)}
          </div>
          <p className="text-sm mb-6">{status.message}</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg bg-card p-4">
              <span className="text-xs text-muted-foreground">Expected Today</span>
              <p className="text-xl font-semibold">{formatNumber(status.expectedNotifications)}</p>
            </div>
            <div className="rounded-lg bg-card p-4">
              <span className="text-xs text-muted-foreground">Actual Today</span>
              <p className="text-xl font-semibold">{formatNumber(status.actualNotifications)}</p>
            </div>
            <div className="rounded-lg bg-card p-4">
              <span className="text-xs text-muted-foreground">Below Expected</span>
              <p className="text-xl font-semibold text-red-600 dark:text-red-400">{formatNumber(status.missingNotifications)}</p>
            </div>
            <div className="rounded-lg bg-card p-4">
              <span className="text-xs text-muted-foreground">Last Notification</span>
              <p className="text-sm font-medium">{formatDateTime(status.lastNotificationTime)}</p>
            </div>
          </div>

          {status.alerts.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold mb-2">Alerts:</p>
              <div className="flex flex-col gap-2">
                {status.alerts.map((alert, idx) => (
                  <div key={idx} className="rounded-lg bg-card p-3">
                    <p className="text-sm">&bull; {alert}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </MainCard>
      )}

      {stats && (
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-6">
            <MainCard>
              <h3 className="text-lg font-semibold mb-4">Statistics (Last 30 Days)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-muted-foreground">Total Notifications</span>
                  <p className="text-xl font-semibold">{formatNumber(stats.total)}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Today</span>
                  <p className="text-xl font-semibold">{formatNumber(stats.today)}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Last 7 Days</span>
                  <p className="text-xl font-semibold">{formatNumber(stats.last7Days)}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Daily Average</span>
                  <p className="text-xl font-semibold">{formatNumber(stats.averageDaily)}</p>
                </div>
              </div>
            </MainCard>
          </div>

          <div className="col-span-12 lg:col-span-6">
            <MainCard>
              <h3 className="text-lg font-semibold mb-4">Peak Hours</h3>
              {(() => {
                const currentDayOfWeek = new Date().getDay();
                const dayName = dayNames[currentDayOfWeek];
                const currentDayPeakHours = stats.peakHoursByDay?.[currentDayOfWeek] || stats.peakHours;
                const currentDayHourlyPatterns = stats.hourlyPatternsByDay?.[currentDayOfWeek] || stats.hourlyPatterns;
                
                if (currentDayPeakHours.length > 0) {
                  return (
                    <div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Peak hours for {dayName} (based on historical patterns)
                      </p>
                      <div className="flex flex-col gap-2">
                        {currentDayPeakHours.map((hour) => {
                          const pattern = currentDayHourlyPatterns.find((p) => p.hour === hour);
                          const nextHour = (hour + 1) % 24;
                          const rangeLabel = `${formatHour(hour)} - ${formatHour(nextHour)}`;
                          return (
                            <div key={hour} className="flex justify-between">
                              <span className="text-sm font-medium">{rangeLabel}</span>
                              <span className="text-xs text-muted-foreground">
                                Avg: {formatNumber(pattern?.averageCount || 0)} per hour
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <p className="text-sm text-muted-foreground">
                      No peak hours identified for {dayName}
                    </p>
                  );
                }
              })()}
            </MainCard>
          </div>

          <div className="col-span-12">
            <MainCard>
              <h3 className="text-lg font-semibold mb-4">Daily Averages by Day of Week</h3>
              <div className="flex gap-2">
                {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                  <div key={day} className="flex-1 text-center">
                    <span className="text-xs text-muted-foreground">
                      {dayNames[day].slice(0, 3)}
                    </span>
                    <p className="text-lg font-semibold">
                      {formatNumber(stats.dailyAverages[day] || 0)}
                    </p>
                  </div>
                ))}
              </div>
            </MainCard>
          </div>

          {stats.byDate.length > 0 && (
            <div className="col-span-12">
              <MainCard>
                <h3 className="text-lg font-semibold mb-4">Notifications by Date</h3>
                <div className="max-h-[400px] overflow-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-card">
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Count</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stats.byDate.map((item) => (
                        <TableRow key={item.date}>
                          <TableCell>{formatDate(item.date)}</TableCell>
                          <TableCell className="font-medium">{item.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </MainCard>
            </div>
          )}
        </div>
      )}

      {!isLoading && !statsLoading && !status && !stats && !error && (
        <MainCard>
          <p className="text-sm text-muted-foreground text-center py-8">
            No data available
          </p>
        </MainCard>
      )}
    </div>
  );
}
